/**
 * Admin Portal → Partners → a partner → API keys (codes platform item C6).
 *
 * The keys a partner's developer uses for the partner self-serve API
 * (`/api/partner/v1`, docs/partners/PARTNER_API.md): create one (the full key is shown
 * HERE ONCE — the server keeps only its hash, and this page keeps it only in memory until
 * "I've saved it"), list them by their public prefix, rotate one (a new key; the old one
 * keeps working 24 hours) and revoke one (immediately).
 *
 * The list is open to every admin role; create, rotate and revoke need an admin or super
 * admin session (the server answers 403 otherwise, and these controls hide).
 *
 * Web-only (the admin SPA).
 */

import React, { useCallback, useEffect, useState } from 'react';
import { AdminButton, AdminCard } from '../../../components/admin/ui';
import { PartnersAdmin } from '../../../services/partnersAdminService';
import {
  API_KEY_CONTACT_QUOTA_MAX,
  API_KEY_SCOPES,
  API_KEY_SCOPE_LABELS,
  CONTACT_SCOPES,
  PARTNER_API_BASE,
  apiKeyActionable,
  apiKeyBody,
  apiKeyLimitsText,
  apiKeyStateBadge,
  emptyApiKeyForm,
  hasContactScope,
  type ApiKeyForm,
  type ApiKeyRow,
} from './partnersLogic';
import {
  NoticeLine,
  StatusBadge,
  TypedConfirm,
  copyText,
  errText,
  fmtDateTime,
  hint,
  input,
  label,
  row,
  td,
  th,
  useBusy,
  type Notice,
} from './PartnersShared';

/** The key the server just returned — held in memory only, until the admin dismisses it. */
interface Revealed {
  secret: string;
  prefix: string;
  why: 'created' | 'rotated';
}

export const PartnerApiKeysPanel: React.FC<{
  partnerId: string;
  partnerEnded: boolean;
  writable: boolean;
}> = ({ partnerId, partnerEnded, writable }) => {
  const [keys, setKeys] = useState<ApiKeyRow[]>([]);
  const [notice, setNotice] = useState<Notice>(null);
  const [busy, run] = useBusy();
  const [form, setForm] = useState<ApiKeyForm>(emptyApiKeyForm());
  const [revealed, setRevealed] = useState<Revealed | null>(null);
  const [copied, setCopied] = useState(false);
  const [revoking, setRevoking] = useState<ApiKeyRow | null>(null);
  const [rotating, setRotating] = useState<ApiKeyRow | null>(null);

  const load = useCallback(async () => {
    try {
      const res = await PartnersAdmin.listApiKeys(partnerId);
      setKeys(res.keys || []);
    } catch (e) {
      setNotice({ kind: 'error', text: errText(e) });
    }
  }, [partnerId]);

  useEffect(() => { load(); }, [load]);

  const reveal = (secret: string, prefix: string, why: Revealed['why']) => {
    setCopied(false);
    setRevealed({ secret, prefix, why });
  };

  const create = () =>
    run(async () => {
      const { body, errors } = apiKeyBody(form);
      if (errors.length) { setNotice({ kind: 'error', text: 'Not created:', extra: errors }); return; }
      try {
        const res = await PartnersAdmin.createApiKey(partnerId, body);
        reveal(res.secret, res.key.key_prefix, 'created');
        setForm(emptyApiKeyForm());
        setNotice(null);
        await load();
      } catch (e) {
        setNotice({ kind: 'error', text: errText(e) });
      }
    });

  const rotate = () =>
    run(async () => {
      if (!rotating) return;
      try {
        const res = await PartnersAdmin.rotateApiKey(rotating.id);
        reveal(res.secret, res.key.key_prefix, 'rotated');
        setNotice({ kind: 'ok', text: `${rotating.key_prefix} keeps working for 24 hours; then only ${res.key.key_prefix} does.` });
        setRotating(null);
        await load();
      } catch (e) {
        setNotice({ kind: 'error', text: errText(e) });
      }
    });

  const revoke = () =>
    run(async () => {
      if (!revoking) return;
      try {
        const res = await PartnersAdmin.revokeApiKey(revoking.id);
        setNotice({ kind: 'ok', text: res.changed ? `Revoked ${revoking.key_prefix}: every call with it is refused from now on.` : `${revoking.key_prefix} was already revoked.` });
        setRevoking(null);
        await load();
      } catch (e) {
        setNotice({ kind: 'error', text: errText(e) });
      }
    });

  const copy = async () => {
    if (revealed) setCopied(await copyText(revealed.secret));
  };

  return (
    <AdminCard>
      <h3 style={{ margin: '0 0 8px', fontSize: 15 }}>API keys</h3>
      <div style={hint}>
        For the partner's own developer: they create single-use codes, read each code's state and read their numbers
        through the partner API (<code>{PARTNER_API_BASE}</code>, key in the <code>X-Partner-Key</code> header). Send
        them the developer page (docs/partners/PARTNER_API.md) with the key. A key works only while the program is on.
      </div>
      <NoticeLine notice={notice} onClose={() => setNotice(null)} />

      {revealed && (
        <div role="alert" aria-label="New API key" style={{ margin: '12px 0', padding: 12, borderRadius: 10, border: '1px solid var(--warning, #d97706)', background: 'var(--surface-subtle, #fffbeb)' }}>
          <b>{revealed.why === 'rotated' ? 'The new key' : 'The key'} — copy it now. It will not be shown again.</b>
          <div style={{ fontSize: 12, margin: '6px 0' }}>
            Coach Cedar keeps only a one-way hash of it. Give it to the partner's developer through a private channel
            (never in a ticket, a chat channel or a shared document). If it is lost, rotate the key.
          </div>
          <div style={{ ...row, alignItems: 'center' }}>
            <code data-testid="api-key-secret" style={{ wordBreak: 'break-all', fontSize: 13 }}>{revealed.secret}</code>
            <AdminButton size="sm" variant="secondary" onClick={copy}>{copied ? 'Copied' : 'Copy'}</AdminButton>
            <AdminButton size="sm" onClick={() => setRevealed(null)}>I've saved it</AdminButton>
          </div>
        </div>
      )}

      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={th}>Key</th><th style={th}>Name</th><th style={th}>Scopes</th><th style={th}>Limits</th>
              <th style={th}>State</th><th style={th}>Last used</th><th style={th}>Last 24 h</th><th style={th}>Created</th>
              {writable && <th style={th} />}
            </tr>
          </thead>
          <tbody>
            {keys.length === 0 && <tr><td style={td} colSpan={writable ? 9 : 8}>No API keys.</td></tr>}
            {keys.map((k) => (
              <tr key={k.id}>
                <td style={td}><code>{k.key_prefix}</code></td>
                <td style={td}>{k.name}</td>
                <td style={td}>{k.scopes.join(', ')}</td>
                <td style={td}>{apiKeyLimitsText(k)}</td>
                <td style={td}>
                  <StatusBadge status={k.state} tone={apiKeyStateBadge(k.state)} />
                  {k.state === 'expiring' && k.expires_at ? <div style={hint}>until {fmtDateTime(k.expires_at)}</div> : null}
                </td>
                <td style={td}>{k.last_used_at ? fmtDateTime(k.last_used_at) : 'never'}</td>
                <td style={td}>{`${k.calls_24h ?? 0} calls · ${k.codes_created_24h ?? 0} codes`}</td>
                <td style={td}>{fmtDateTime(k.created_at)}<div style={hint}>{k.created_by}</div></td>
                {writable && (
                  <td style={{ ...td, whiteSpace: 'nowrap' }}>
                    {apiKeyActionable(k) && (
                      <>
                        {k.state === 'active' && !partnerEnded && (
                          <AdminButton size="sm" variant="ghost" onClick={() => setRotating(k)}>Rotate</AdminButton>
                        )}
                        <AdminButton size="sm" variant="ghost" onClick={() => setRevoking(k)}>Revoke</AdminButton>
                      </>
                    )}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {writable && !partnerEnded && (
        <div style={{ marginTop: 16 }}>
          <b style={{ fontSize: 13 }}>New key</b>
          <div style={{ ...row, marginTop: 6 }}>
            <div>
              <label style={label} htmlFor="ak-name">Name (who uses it)</label>
              <input id="ak-name" style={{ ...input, width: 220 }} value={form.name} placeholder="Run Club website"
                onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </div>
            <div>
              <label style={label} htmlFor="ak-rate">Calls per minute</label>
              <input id="ak-rate" style={{ ...input, width: 90 }} value={form.rateLimit}
                onChange={(e) => setForm({ ...form, rateLimit: e.target.value })} />
            </div>
            <div>
              <label style={label} htmlFor="ak-quota">Codes per day</label>
              <input id="ak-quota" style={{ ...input, width: 100 }} value={form.dailyQuota}
                onChange={(e) => setForm({ ...form, dailyQuota: e.target.value })} />
            </div>
            <AdminButton size="sm" onClick={create} disabled={busy}>Create key</AdminButton>
          </div>
          <div style={{ ...row, marginTop: 8 }}>
            {API_KEY_SCOPES.filter((s) => !(CONTACT_SCOPES as readonly string[]).includes(s)).map((s) => (
              <label key={s} style={{ fontSize: 13 }}>
                <input type="checkbox" checked={form.scopes[s]}
                  onChange={(e) => setForm({ ...form, scopes: { ...form.scopes, [s]: e.target.checked } })} /> {API_KEY_SCOPE_LABELS[s]} (<code>{s}</code>)
              </label>
            ))}
          </div>
          <div data-testid="api-key-contact-scopes" style={{ marginTop: 10, paddingTop: 8, borderTop: '1px dashed var(--border, #e5e7eb)' }}>
            <div style={{ ...hint, marginTop: 0 }}>
              Contacts — personal data, off unless ticked. Only for a partner whose developer will send us people who agreed to
              hear from Coach Cedar; every call carries the consent attestation.
            </div>
            <div style={{ ...row, marginTop: 6 }}>
              {CONTACT_SCOPES.map((s) => (
                <label key={s} style={{ fontSize: 13 }}>
                  <input type="checkbox" checked={form.scopes[s]}
                    onChange={(e) => setForm({ ...form, scopes: { ...form.scopes, [s]: e.target.checked } })} /> {API_KEY_SCOPE_LABELS[s]} (<code>{s}</code>)
                </label>
              ))}
              <div>
                <label style={label} htmlFor="ak-contact-quota">Contacts per day</label>
                <input id="ak-contact-quota" style={{ ...input, width: 100 }} value={form.dailyContactQuota}
                  disabled={!hasContactScope(API_KEY_SCOPES.filter((s) => form.scopes[s]))}
                  onChange={(e) => setForm({ ...form, dailyContactQuota: e.target.value })} />
              </div>
            </div>
            <div style={hint}>Up to {API_KEY_CONTACT_QUOTA_MAX.toLocaleString('en-CA')} a day; sent only with a contacts scope. Emails to them still wait for Send on the Contacts tab.</div>
          </div>
        </div>
      )}
      {partnerEnded && <div style={hint}>This partner has ended: its keys are refused and no new key can be made.</div>}

      {revoking && (
        <TypedConfirm
          title={`Revoke ${revoking.key_prefix}?`}
          body={<>Every call with this key is refused from now on. The partner's systems stop minting and reading codes until they get a new key. Codes it already made keep working.</>}
          expected={revoking.key_prefix}
          confirmLabel="Revoke key"
          busy={busy}
          onConfirm={revoke}
          onCancel={() => setRevoking(null)}
        />
      )}
      {rotating && (
        <TypedConfirm
          title={`Rotate ${rotating.key_prefix}?`}
          body={<>A new key with the same scopes and limits is shown once. The old key keeps working for 24 hours so the partner can swap it, then stops.</>}
          expected={rotating.key_prefix}
          confirmLabel="Rotate key"
          danger={false}
          busy={busy}
          onConfirm={rotate}
          onCancel={() => setRotating(null)}
        />
      )}
    </AdminCard>
  );
};
